import { CookieConsent, CookieConsentLanguage, SettingsModalBlock } from '@aipb/frame/model';
import { CONFIG_COOKIE_CONSENT, CONFIG_FRAME, CONFIG_GOOGLE_ANALYTICS_ID } from '@aipb/util/di';
import { isNil } from '@aipb/util/other';
import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { GoogleAnalyticsInitializer, NGX_GTAG_FN } from 'ngx-google-analytics';
import { SetupHandler } from './setup.handler';

declare let initCookieConsent: any;

@Injectable({
  providedIn: 'root'
})
export class CookieConsentHandler extends SetupHandler {
  private transloco = inject(TranslocoService);
  private gtag = inject(NGX_GTAG_FN);
  private document = inject(DOCUMENT);
  private cookieConsent = inject(CONFIG_COOKIE_CONSENT);
  private frame = inject(CONFIG_FRAME);
  private googleAnalyticsId = inject(CONFIG_GOOGLE_ANALYTICS_ID);

  constructor() {
    super('cookie-consent.handler');
  }

  handle() {
    if (!this.cookieConsent) return;

    const cc = initCookieConsent();

    const cookieLanguages = {};

    // @ts-expect-error foo
    this.frame.languages.forEach((lang) => {
      // @ts-expect-error foo
      cookieLanguages[lang.name] = this.createCookieConsentLanguage(lang.name);
    });

    const config: CookieConsent = {
      current_lang: this.transloco.getActiveLang(),
      force_consent: isNil(this.cookieConsent?.force_consent) ? false : this.cookieConsent.force_consent,
      autoclear_cookies: true,
      remove_cookie_tables: true,
      revision: this.cookieConsent?.revision || 0,
      onAccept: (cookie) => {
        // Initialize Google Analytics, if accepted in cookies
        if (cc.allowedCategory('analytics') && this.googleAnalyticsId) {
          GoogleAnalyticsInitializer({ trackingCode: this.googleAnalyticsId }, this.gtag, this.document)().then();
        }
      },
      languages: cookieLanguages
    };

    cc.run({
      ...config,
      hide_from_bots: false
    });

    this.transloco.langChanges$.subscribe((lang) => {
      if (!cc) return;

      cc.updateLanguage(lang);
    });
  }

  private createCookieConsentLanguage(lang: string): CookieConsentLanguage {
    const translation = this.transloco.getTranslation(lang);

    const blocks: SettingsModalBlock[] = [
      {
        title: translation['cookie_consent.settings_modal.blocks.intro.title'],
        description: translation['cookie_consent.settings_modal.blocks.intro.description']
      },
      {
        title: translation['cookie_consent.settings_modal.blocks.necessary.title'],
        description: translation['cookie_consent.settings_modal.blocks.necessary.description'],
        toggle: {
          value: 'necessary',
          enabled: true,
          readonly: true // cookie categories with readonly=true are all treated as "necessary cookies"
        }
      }
    ];

    if (this.googleAnalyticsId) {
      blocks.push({
        title: translation['cookie_consent.settings_modal.blocks.analytics.title'],
        description: translation['cookie_consent.settings_modal.blocks.analytics.description'],
        toggle: {
          value: 'analytics', // your cookie category
          enabled: false,
          readonly: false
        },
        cookie_table: [
          // list of all expected cookies
          {
            col1: '^_ga', // match all cookies starting with "_ga"
            col2: 'google.com',
            col3: '2 years',
            col4: 'description ...',
            is_regex: true
          },
          {
            col1: '_gid',
            col2: 'google.com',
            col3: '1 day',
            col4: 'description ...'
          }
        ]
      });
    }

    return {
      consent_modal: {
        title: translation['cookie_consent.consent_modal.title'],
        description: translation['cookie_consent.consent_modal.description'],
        primary_btn: {
          text: translation['cookie_consent.consent_modal.accept_all'],
          role: 'accept_all' // 'accept_selected' or 'accept_all'
        },
        secondary_btn: {
          text: translation['cookie_consent.consent_modal.deny_all'],
          role: 'accept_necessary' // 'settings' or 'accept_necessary'
        }
      },
      settings_modal: {
        title: translation['cookie_consent.settings_modal.title'],
        save_settings_btn: translation['cookie_consent.settings_modal.save_settings_btn'],
        accept_all_btn: translation['cookie_consent.settings_modal.accept_all_btn'],
        reject_all_btn: translation['cookie_consent.settings_modal.reject_all_btn'],
        close_btn_label: translation['cookie_consent.settings_modal.close_btn_label'],
        cookie_table_headers: [
          { col1: translation['cookie_consent.settings_modal.cookie_table_headers.col1'] },
          { col2: translation['cookie_consent.settings_modal.cookie_table_headers.col2'] },
          { col3: translation['cookie_consent.settings_modal.cookie_table_headers.col3'] },
          { col4: translation['cookie_consent.settings_modal.cookie_table_headers.col4'] }
        ],
        blocks: blocks
      }
    };
  }
}
